import { useContext, useEffect, useState } from "react";
import { NamedProperty, DropdownOption, mapToDropdownOption, getById, ValidationResult } from "sprint-common-web";

import { useAirlines } from "../../../../../../common/data/data-hooks/useArilines";
import { useNonTrailerVersionTypes } from "../../../../../../common/data/data-hooks/useNonTrailerVersionTypes";
import { useTrailerVersionTypes } from "../../../../../../common/data/data-hooks/useTrailerVersionTypes";
import {
    Distributor,
    DistributorsDataContext
} from "../../../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { UpdateEpisodeVersionRequest } from "../../../../api/UpdateEpisodeVersionRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { ValidateUpdateEpisodeVersionRequest } from "../../../../api/ValidateUpdateEpisodeVersionRequest";
import { EpisodeVersionDetails } from "../../../../TvSeriesDetails";
import { EpisodeVersionListContext } from "../context/EpisodeVersionListProvider";
import { UpdateEpisodeVersionParams } from "./EditEpisodeVersionForm";
import { EditEpisodeVersionFormInputs } from "./EditEpisodeVersionFormInputs";

interface EpisodeVersionValues {
    distributor: Distributor;
    versionType: NamedProperty;
    code?: string;
}

export const useEditEpisodeVersionForm = (selectedEpisodeVersionId: string) => {
    const { selectedEpisode, selectedTvSeries } = useContext(EpisodeVersionListContext);
    const { data: airlines } = useAirlines();
    const { data: trailerVersionTypes } = useTrailerVersionTypes();
    const { data: nonTrailerVersionTypes } = useNonTrailerVersionTypes();
    const { getDistributorById } = useContext(DistributorsDataContext);

    const selectedEpisodeVersion = getById(selectedEpisode.versions, selectedEpisodeVersionId);

    const [isTrailerSelected, setIsTrailerSelected] = useState(
        !!trailerVersionTypes?.find((v) => v.id === selectedEpisodeVersion.type.id)
    );

    useEffect(() => {
        setIsTrailerSelected(!!trailerVersionTypes?.find((v) => v.id === selectedEpisodeVersion.type.id));
    }, [trailerVersionTypes]);

    const versionTypes = isTrailerSelected ? trailerVersionTypes : nonTrailerVersionTypes;

    const { validateUpdateEpisodeVersion, updateEpisodeVersion } = useAssetsManageTvSeriesApi();
    const { defaultValues, defaultOptions } = getDefaultValues(selectedEpisodeVersion);

    const validateEditEpisodeVersion = async (inputs: EditEpisodeVersionFormInputs): Promise<ValidationResult> => {
        const request = createValidateUpdateVersionRequest(selectedTvSeries.id, inputs);

        return validateUpdateEpisodeVersion(selectedEpisode.id, selectedEpisodeVersion.id, request);
    };

    const editEpisodeVersion = async (inputs: EditEpisodeVersionFormInputs): Promise<UpdateEpisodeVersionParams> => {
        let distributor = selectedEpisodeVersion.distributor;
        if (selectedEpisodeVersion.distributor.id !== inputs.distributorId) {
            distributor = await getDistributorById(inputs.distributorId);
        }

        const versionType = getById(versionTypes ?? [], inputs.versionTypeId);

        const code = inputs.code;

        return updateEpisodeVersion(
            selectedEpisode.id,
            selectedEpisodeVersion.id,
            createUpdateVersionRequest(selectedTvSeries.id, { distributor, versionType, code })
        ).then(() => getUpdatedVersionData({ distributor, versionType, code }));
    };

    const getUpdatedVersionData = (valuesToUpdate: EpisodeVersionValues): UpdateEpisodeVersionParams => {
        return {
            versionId: selectedEpisodeVersion.id,
            distributor: {
                id: valuesToUpdate.distributor.id,
                name: valuesToUpdate.distributor.name
            },
            type: {
                id: valuesToUpdate.versionType.id,
                name: valuesToUpdate.versionType.name
            },
            code: valuesToUpdate.code,
            isTrailer: isTrailerSelected
        };
    };

    return {
        airlines: airlines ?? [],
        versionTypes,
        trailerVersionTypes,
        selectedEpisodeVersion,
        isTrailerSelected,
        setIsTrailerSelected,
        defaultOptions,
        defaultValues,
        validateEditEpisodeVersion,
        editEpisodeVersion
    };
};

const createValidateUpdateVersionRequest = (
    tvSeriesId: string,
    inputs: EditEpisodeVersionFormInputs
): ValidateUpdateEpisodeVersionRequest => ({
    tvSeriesId,
    typeId: inputs.versionTypeId,
    distributorId: inputs.distributorId,
    brandingIds: inputs.brandingIds
});

const createUpdateVersionRequest = (
    tvSeriesId: string,
    valuesToUpdate: EpisodeVersionValues
): UpdateEpisodeVersionRequest => {
    return {
        tvSeriesId,
        distributorId: valuesToUpdate.distributor.id,
        typeId: valuesToUpdate.versionType.id,
        code: valuesToUpdate.code
    };
};

interface EditEpisodeVersionFormDefaults {
    defaultValues: EditEpisodeVersionFormInputs;
    defaultOptions: {
        distributors: DropdownOption[];
    };
}

const getDefaultValues = (selectedEpisodeVersion?: EpisodeVersionDetails): EditEpisodeVersionFormDefaults => {
    if (selectedEpisodeVersion) {
        return {
            defaultValues: {
                distributorId: selectedEpisodeVersion.distributor.id,
                brandingIds: selectedEpisodeVersion.brandings?.map((item) => item.id) ?? [],
                versionTypeId: selectedEpisodeVersion.type.id,
                code: selectedEpisodeVersion.code
            },
            defaultOptions: {
                distributors: [mapToDropdownOption(selectedEpisodeVersion.distributor)]
            }
        };
    }

    return {
        defaultValues: { distributorId: "", versionTypeId: "", brandingIds: [], code: "" },
        defaultOptions: { distributors: [] }
    };
};
